import { useState } from "react";
import { ArrowRight, Eye, EyeOff, Lock, Loader2, Mail } from "lucide-react";

export default function LoginPage({ onLogin, loading, error }) {
  const [username, setUsername] = useState("tech@genia.media");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (preparing || loading) return;
    setPreparing(true);
    await new Promise((resolve) => setTimeout(resolve, 2500));
    try {
      await onLogin(username, password);
    } finally {
      setPreparing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] text-slate-900 p-[2px]">
      <div className="min-h-[calc(100vh-4px)] rounded-[28px] overflow-hidden relative bg-[linear-gradient(180deg,#f58204_0%,#f8b15d_22%,#f6d7aa_44%,#f8f4ea_72%,#ffffff_100%)] border border-slate-200">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_74%,rgba(255,255,255,0.9),transparent_16%),radial-gradient(circle_at_84%_76%,rgba(255,255,255,0.92),transparent_16%),radial-gradient(circle_at_36%_58%,rgba(255,255,255,0.55),transparent_10%),radial-gradient(circle_at_64%_56%,rgba(255,255,255,0.52),transparent_10%),linear-gradient(180deg,rgba(255,255,255,0.18),rgba(255,255,255,0.0)_45%,rgba(255,255,255,0.55)_100%)]" />

        <div className="relative h-full min-h-[calc(100vh-4px)]">
          <div className="absolute left-16 top-8 flex items-center gap-3">
            <img
              src="/logo-gm.png"
              alt="Logo"
              className="h-11 w-11 rounded-xl object-cover shadow-[0_12px_24px_rgba(15,23,42,0.18)]"
            />
            <span className="text-xl font-semibold tracking-tight text-white drop-shadow-[0_1px_2px_rgba(15,23,42,0.25)]">
              SaaS DCO
            </span>
          </div>

          <div className="absolute left-1/2 top-1/2 w-[460px] max-w-[calc(100vw-48px)] -translate-x-1/2 -translate-y-1/2 rounded-[26px] bg-[linear-gradient(180deg,rgba(245,252,255,0.98)_0%,rgba(255,255,255,0.96)_100%)] border border-slate-200/70 shadow-[0_18px_45px_rgba(15,23,42,0.15)] px-8 py-8">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] border border-slate-200">
              <ArrowRight className="h-6 w-6 text-slate-700" />
            </div>

            <h1 className="text-center text-3xl font-semibold tracking-tight text-slate-900">
              Connexion
            </h1>
            <p className="mt-2 text-center text-[16px] leading-6 text-slate-500">
              Accédez à votre espace pour lancer vos créations.
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-500">E-mail</span>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="tech@genia.media"
                    className="h-[52px] w-full rounded-xl border-0 bg-[#f3f6f9] pl-11 pr-4 text-base text-slate-900 outline-none ring-1 ring-inset ring-transparent transition placeholder:text-slate-400 focus:ring-[#f58207]"
                    autoComplete="username"
                    data-testid="login-username-input"
                  />
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-500">Mot de passe</span>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mot de passe"
                    className="h-[52px] w-full rounded-xl border-0 bg-[#f3f6f9] pl-11 pr-12 text-base text-slate-900 outline-none ring-1 ring-inset ring-transparent transition placeholder:text-slate-400 focus:ring-[#f58207]"
                    autoComplete="current-password"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-400 hover:text-slate-700"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </label>

              {error && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || preparing}
                data-testid="login-submit-button"
                className="mt-3 h-[52px] w-full rounded-xl bg-[#13151f] text-white text-lg font-medium flex items-center justify-center gap-3 shadow-[0_14px_30px_rgba(15,23,42,0.18)] transition-colors hover:bg-[#0e1018] active:bg-[#0b0d14] disabled:opacity-60"
              >
                {loading || preparing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-5 w-5" />
                    Se connecter
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
